import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { getVendorMe, updateMyVendorProfile, upsertMyVendorContact } from "@/lib/vendor-portal.functions";

export const Route = createFileRoute("/vendor/profile")({
  component: VendorProfile,
});

const LEVELS = ["L1", "L2", "L3", "L4"] as const;

function VendorProfile() {
  const qc = useQueryClient();
  const me = useServerFn(getVendorMe);
  const upd = useServerFn(updateMyVendorProfile);
  const upsertContact = useServerFn(upsertMyVendorContact);

  const { data, isLoading } = useQuery({ queryKey: ["vendor", "me"], queryFn: () => me({}) });

  const [form, setForm] = useState<any>({});
  const [contacts, setContacts] = useState<any>({});

  useEffect(() => {
    if (!data?.vendor) return;
    const v = data.vendor as any;
    setForm({
      address: v.address ?? "", city: v.city ?? "", state: v.state ?? "", pincode: v.pincode ?? "",
      phone: v.phone ?? "", website: v.website ?? "", gstin: v.gstin ?? "", pan: v.pan ?? "",
    });
    const byLevel: any = {};
    for (const l of LEVELS) {
      const c = (data.contacts as any[]).find((x) => x.level === l);
      byLevel[l] = c ? { name: c.name ?? "", designation: c.designation ?? "", phone: c.phone ?? "", email: c.email ?? "" } : { name: "", designation: "", phone: "", email: "" };
    }
    setContacts(byLevel);
  }, [data]);

  const saveProfile = useMutation({
    mutationFn: () => upd({ data: form }),
    onSuccess: () => { toast.success("Profile updated"); qc.invalidateQueries({ queryKey: ["vendor", "me"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const saveContact = useMutation({
    mutationFn: (level: string) => upsertContact({ data: { level: level as any, ...contacts[level] } }),
    onSuccess: () => { toast.success("Contact saved"); qc.invalidateQueries({ queryKey: ["vendor", "me"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <p>Loading…</p>;
  const v = data?.vendor as any;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">My Profile</h1>
        <p className="text-sm text-muted-foreground">{v?.name} · {v?.code} · {v?.email}</p>
      </div>

      <section className="rounded-md border bg-card p-4">
        <h2 className="mb-4 text-lg font-semibold">Business Details</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {(["gstin","pan","phone","website","address","city","state","pincode"] as const).map((k) => (
            <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
              <Label className="text-xs uppercase">{k}</Label>
              {k === "address" ? (
                <Textarea rows={2} value={form[k] ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, [k]: e.target.value }))} />
              ) : (
                <Input value={form[k] ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, [k]: e.target.value }))} />
              )}
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={() => saveProfile.mutate()} disabled={saveProfile.isPending}>Save Details</Button>
        </div>
      </section>

      <section className="rounded-md border bg-card p-4">
        <h2 className="mb-4 text-lg font-semibold">Escalation Contacts</h2>
        <div className="space-y-4">
          {LEVELS.map((l) => (
            <div key={l} className="rounded border p-3">
              <div className="mb-2 flex items-center justify-between">
                <p className="font-semibold">{l}</p>
                <Button size="sm" variant="outline" onClick={() => saveContact.mutate(l)}>Save {l}</Button>
              </div>
              <div className="grid gap-2 sm:grid-cols-4">
                <Input placeholder="Name" value={contacts[l]?.name ?? ""} onChange={(e) => setContacts((c: any) => ({ ...c, [l]: { ...c[l], name: e.target.value } }))} />
                <Input placeholder="Designation" value={contacts[l]?.designation ?? ""} onChange={(e) => setContacts((c: any) => ({ ...c, [l]: { ...c[l], designation: e.target.value } }))} />
                <Input placeholder="Phone" value={contacts[l]?.phone ?? ""} onChange={(e) => setContacts((c: any) => ({ ...c, [l]: { ...c[l], phone: e.target.value } }))} />
                <Input placeholder="Email" value={contacts[l]?.email ?? ""} onChange={(e) => setContacts((c: any) => ({ ...c, [l]: { ...c[l], email: e.target.value } }))} />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
